/**
 * iOS Safari cart fixes — customized built-in elements (is="product-form", etc.)
 * are unreliable in Safari. When ProductForm fails to upgrade, taps/submits do nothing.
 *
 * Fallback: direct /cart/add.js fetch + cart events (mirrors theme ProductForm).
 */
(() => {
  const TAP_MOVE_THRESHOLD = 12;

  const OVERLAY_BLOCKERS = [
    "#CybotCookiebotDialog",
    "#CybotCookiebotDialogBodyUnderlay",
    ".rebuy-cart",
    ".rebuy-modal",
    ".smart-cart--enabled .rebuy-cart",
    ".cart-dev-preview-banner",
    "privacy-banner",
    ".shopify-pc__banner",
    ".needsclick.kl-private",
    "[id*='cookie']",
    "[class*='cookie-banner']",
    "[class*='CookieConsent']"
  ].join(",");

  const CART_FORM_SELECTOR = [
    'form[is="product-form"]',
    "form.pdp-related-card__form",
    "form[data-gat-cart-form]",
    'form[action*="/cart/add"]'
  ].join(",");

  const isTouchDevice = () =>
    document.documentElement.classList.contains("has-touch") ||
    "ontouchstart" in window ||
    (navigator.maxTouchPoints != null && navigator.maxTouchPoints > 0);

  if (
    "ontouchstart" in window ||
    (navigator.maxTouchPoints != null && navigator.maxTouchPoints > 0)
  ) {
    document.documentElement.classList.add("has-touch");
  }

  function isUpgradedProductForm(form) {
    const ProductFormClass = customElements.get("product-form");
    if (!ProductFormClass || !form) {
      return false;
    }
    return form instanceof ProductFormClass;
  }

  function isCartAddForm(form) {
    if (!form || form.tagName !== "FORM") {
      return false;
    }
    if (
      form.matches(CART_FORM_SELECTOR) ||
      form.classList.contains("pdp-related-card__form") ||
      form.hasAttribute("data-gat-cart-form")
    ) {
      return true;
    }
    if (form.getAttribute("is") === "product-form") {
      return true;
    }
    const action = form.getAttribute("action") || "";
    return action.includes("/cart/add");
  }

  function getProductForm(button) {
    if (!button) {
      return null;
    }
    if (button.form) {
      return button.form;
    }
    const formId = button.getAttribute("form");
    return formId ? document.getElementById(formId) : null;
  }

  function isBlockedOverlayTarget(target) {
    return Boolean(target?.closest?.(OVERLAY_BLOCKERS));
  }

  function isScrollableSurface(node) {
    return Boolean(
      node?.closest?.(".scroll-area, scroll-carousel, .scroll-area.is-scrollable")
    );
  }

  function isProductSubmitButton(button) {
    if (!button || button.disabled || button.type !== "submit") {
      return false;
    }
    if (isBlockedOverlayTarget(button)) {
      return false;
    }
    const form = getProductForm(button);
    return isCartAddForm(form);
  }

  function cartRoot() {
    return window.Shopify?.routes?.root || "/";
  }

  const ADDED_LABEL = "Added to Cart!";
  const ADDED_RESET_MS = 2200;
  const PENDING_CART_ADD_MS = 8000;

  /** Last card ATC attempt — used when Rebuy or other handlers swallow the form submit event. */
  let pendingCartAdd = null;

  function markPendingCartAdd(form, button = null) {
    if (!form) {
      return;
    }

    pendingCartAdd = {
      form,
      button: button || findSubmitButton(form),
      at: Date.now()
    };
  }

  function consumePendingCartAdd() {
    if (!pendingCartAdd) {
      return null;
    }

    if (Date.now() - pendingCartAdd.at > PENDING_CART_ADD_MS) {
      pendingCartAdd = null;
      return null;
    }

    const pending = pendingCartAdd;
    pendingCartAdd = null;
    return pending;
  }

  function isCardCartForm(form) {
    return Boolean(
      form &&
        (form.closest("pdp-related-card") ||
          form.classList.contains("pdp-related-card__form") ||
          form.hasAttribute("data-gat-cart-form"))
    );
  }

  function findSubmitButton(form) {
    if (!form) {
      return null;
    }
    return (
      form.querySelector(".pdp-atc-row__button button[type='submit']") ||
      form.querySelector("button[type='submit']")
    );
  }

  function defaultAtcLabel(form, button) {
    const card = form?.closest("pdp-related-card");
    if (card?.dataset?.atcLabel) {
      return card.dataset.atcLabel;
    }
    if (form?.dataset?.atcLabel) {
      return form.dataset.atcLabel;
    }
    if (button?.dataset?.gatDefaultLabel) {
      return button.dataset.gatDefaultLabel;
    }

    const contentEl =
      button?.contentElement || button?.querySelector(":scope > div:first-child");
    if (contentEl?.textContent?.trim()) {
      return contentEl.textContent.trim();
    }

    return button?.textContent?.trim() || "Add to cart";
  }

  function setButtonLabel(button, label) {
    if (!button) {
      return;
    }

    const contentEl =
      button.contentElement || button.querySelector(":scope > div:first-child");

    if (contentEl) {
      contentEl.textContent = label;
      return;
    }

    button.textContent = label;
  }

  function showAddedSuccess(form, button = null) {
    const submitButton = button || findSubmitButton(form);
    if (!submitButton || submitButton.classList.contains("button--added-success")) {
      return;
    }

    if (!submitButton.dataset.gatDefaultLabel) {
      submitButton.dataset.gatDefaultLabel = defaultAtcLabel(form, submitButton);
    }

    window.clearTimeout(submitButton._gatSuccessTimer);

    const defaultLabel = submitButton.dataset.gatDefaultLabel;
    const restoreDisabled = submitButton.disabled;

    submitButton.dataset.gatSuccessActive = "true";
    submitButton.classList.add("button--added-success");
    submitButton.disabled = true;
    submitButton.setAttribute("aria-disabled", "true");
    setButtonLabel(submitButton, ADDED_LABEL);

    submitButton._gatSuccessTimer = window.setTimeout(() => {
      delete submitButton.dataset.gatSuccessActive;
      submitButton.classList.remove("button--added-success");
      setButtonLabel(submitButton, defaultLabel);
      submitButton.disabled = restoreDisabled;
      submitButton.setAttribute("aria-disabled", restoreDisabled ? "true" : "false");
      delete submitButton._gatSuccessTimer;
    }, ADDED_RESET_MS);
  }

  function formFromVariantEvent(event) {
    const target = event.target;

    if (target?.tagName === "FORM" && isCartAddForm(target)) {
      return target;
    }

    const closestForm = target?.closest?.("form");
    if (isCartAddForm(closestForm)) {
      return closestForm;
    }

    const cardForm = target
      ?.closest?.("pdp-related-card")
      ?.querySelector("form.pdp-related-card__form, form[data-gat-cart-form]");
    if (isCartAddForm(cardForm)) {
      return cardForm;
    }

    return null;
  }

  function formDataGet(form, name) {
    try {
      return new FormData(form).get(name);
    } catch (error) {
      return "";
    }
  }

  function variantIdFromForm(form) {
    const idInput = form.querySelector('[name="id"], [data-related-variant-id]');
    if (idInput?.value) {
      return String(idInput.value).trim();
    }
    return String(formDataGet(form, "id") || "").trim();
  }

  function normalizeOptionValue(value) {
    return String(value ?? "")
      .replace(/\u00a0/g, " ")
      .trim();
  }

  function parseCardVariants(card) {
    const variantsNode = card?.querySelector("[data-related-variants]");
    if (!variantsNode) {
      return [];
    }

    try {
      // Attribute first — see pdp-related-card.js.
      const raw =
        variantsNode.getAttribute("data-related-variants") ||
        (variantsNode.tagName === "TEMPLATE" && variantsNode.content
          ? variantsNode.content.textContent
          : variantsNode.textContent);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  function cardOptionValue(variant, index) {
    if (!variant) {
      return "";
    }
    if (Array.isArray(variant.options) && variant.options[index] != null) {
      return normalizeOptionValue(variant.options[index]);
    }
    return normalizeOptionValue(variant[`option${index + 1}`]);
  }

  function readCardSelectedOptions(card) {
    const optionsByPosition = new Map();

    card.querySelectorAll("select[data-option-position]").forEach((select) => {
      const position = Number(select.dataset.optionPosition);
      if (!Number.isFinite(position)) {
        return;
      }

      const selectedOption = select.options[select.selectedIndex];
      const value = normalizeOptionValue(selectedOption?.value ?? select.value);
      if (!value) {
        return;
      }

      const isVisible = !select.classList.contains("sr-only");
      if (isVisible || !optionsByPosition.has(position)) {
        optionsByPosition.set(position, value);
      }
    });

    card.querySelectorAll("[data-related-swatch]:checked").forEach((input) => {
      const step = input.closest("[data-option-step]");
      const position = Number(step?.dataset?.optionStep);
      if (Number.isFinite(position)) {
        optionsByPosition.set(position, normalizeOptionValue(input.value));
      }
    });

    return Array.from(optionsByPosition.entries())
      .sort((a, b) => a[0] - b[0])
      .map((entry) => entry[1]);
  }

  function resolveVariantFromCard(card) {
    const variants = parseCardVariants(card);
    if (!variants.length) {
      return null;
    }

    const selectedOptions = readCardSelectedOptions(card);
    if (!selectedOptions.length || selectedOptions.some((value) => !value)) {
      return (
        variants.find((variant) => variant.available === true) ||
        variants[0] ||
        null
      );
    }

    const exact = variants.find((variant) =>
      selectedOptions.every(
        (value, index) => cardOptionValue(variant, index) === value
      )
    );

    if (exact) {
      return exact;
    }

    const scored = variants
      .map((variant) => ({
        variant,
        score: selectedOptions.reduce(
          (total, value, index) =>
            total + (cardOptionValue(variant, index) === value ? 1 : 0),
          0
        )
      }))
      .sort((a, b) => {
        if (b.score !== a.score) {
          return b.score - a.score;
        }
        return Number(b.variant.available === true) - Number(a.variant.available === true);
      });

    return scored[0]?.variant || variants[0] || null;
  }

  function toPreviewSrc(src) {
    if (!src) {
      return src;
    }

    try {
      const url = new URL(src, window.location.href);
      if (
        url.hostname.endsWith("myshopify.com") ||
        url.hostname === "cdn.shopify.com"
      ) {
        return `${url.pathname}${url.search}`;
      }
      return url.href;
    } catch (error) {
      return src;
    }
  }

  function updateCardMedia(card, variant) {
    const imageEl = card.querySelector(".pdp-related-card__image");
    const image = variant?.featured_image;

    if (variant?.url) {
      card.querySelector(".pdp-related-card__media")?.setAttribute("href", variant.url);
      card.querySelector(".pdp-related-card__title-link")?.setAttribute("href", variant.url);
    }

    if (!imageEl || imageEl.tagName !== "IMG" || !image?.src) {
      return;
    }

    const src = toPreviewSrc(image.src);
    imageEl.src = src;

    if (image.srcset) {
      imageEl.srcset = image.srcset
        .split(",")
        .map((entry) => {
          const parts = entry.trim().split(/\s+/);
          parts[0] = toPreviewSrc(parts[0]);
          return parts.join(" ");
        })
        .join(", ");
    } else {
      imageEl.removeAttribute("srcset");
    }
  }

  function applyVariantToCardForm(card, form, variant) {
    if (!variant) {
      return "";
    }

    const idInput = form.querySelector('[name="id"], [data-related-variant-id]');
    if (idInput) {
      idInput.value = String(variant.id);
      idInput.disabled = variant.available !== true;
    }

    const priceEl = card.querySelector("[data-related-price]");
    if (priceEl && variant.price_label) {
      priceEl.textContent = variant.price_label;
    }

    updateCardMedia(card, variant);
    return String(variant.id);
  }

  function syncCardVariant(card) {
    if (!card) {
      return "";
    }

    const form = card.querySelector("form.pdp-related-card__form, form[data-gat-cart-form]");
    if (!form) {
      return "";
    }

    const domVariant = resolveVariantFromCard(card);

    if (typeof card.prepareForCartAdd === "function") {
      const id = card.prepareForCartAdd();
      // Trust the upgraded element only when the live DOM agrees with it.
      if (id && (!domVariant || String(domVariant.id) === String(id))) {
        return id;
      }
    }

    return applyVariantToCardForm(card, form, domVariant);
  }

  async function resolveVariantIdBeforeAdd(form) {
    const card = form.closest("pdp-related-card");
    if (card) {
      const syncedId = syncCardVariant(card);
      if (syncedId) {
        return syncedId;
      }
    }

    const formId = form.id || form.getAttribute("id");
    const picker = formId
      ? document.querySelector(`variant-picker[form-id="${CSS.escape(formId)}"]`)
      : null;

    if (picker && typeof picker.selectCombination === "function") {
      const optionInputs = Array.from(
        document.querySelectorAll(`input[data-option-position][form="${formId}"]:checked`)
      ).sort(
        (a, b) =>
          Number(a.getAttribute("data-option-position")) -
          Number(b.getAttribute("data-option-position"))
      );

      if (optionInputs.length) {
        await picker.selectCombination({
          optionValues: optionInputs.map((input) => input.value),
          productChange: false
        });
      }

      const pickerVariantId = picker.selectedVariant?.id;
      if (pickerVariantId) {
        const idInput = form.querySelector('[name="id"]');
        if (idInput) {
          idInput.value = String(pickerVariantId);
          idInput.disabled = false;
        }
        return String(pickerVariantId);
      }
    }

    const variantId = variantIdFromForm(form);
    if (variantId) {
      const idInput = form.querySelector('[name="id"]');
      if (idInput) {
        idInput.disabled = false;
      }
    }
    return variantId;
  }

  async function addToCartDirect(form, button) {
    if (!form || form.dataset.gatSubmitting === "true") {
      return false;
    }

    if (typeof form.checkValidity === "function" && !form.checkValidity()) {
      form.reportValidity();
      return false;
    }

    const variantId = await resolveVariantIdBeforeAdd(form);
    if (!variantId) {
      return false;
    }

    form.dataset.gatSubmitting = "true";
    if (button) {
      button.setAttribute("aria-busy", "true");
    }

    const formData = new FormData(form);
    formData.set("id", variantId);
    if (!formData.get("quantity")) {
      formData.set("quantity", "1");
    }
    if (!String(formData.get("selling_plan") || "").trim()) {
      formData.delete("selling_plan");
    }

    const root = cartRoot();

    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: "POST",
        body: formData,
        headers: {
          "X-Requested-With": "XMLHttpRequest"
        }
      });

      const responseJson = await response.json();

      if (response.ok) {
        const settings = window.themeVariables?.settings || {};
        if (settings.cartType === "page" || settings.pageType === "cart") {
          window.location.href = `${root}cart`;
          return true;
        }

        const cartContent = await (await fetch(`${root}cart.js`)).json();
        const items = Object.prototype.hasOwnProperty.call(responseJson, "items")
          ? responseJson.items
          : [responseJson];

        form.dispatchEvent(
          new CustomEvent("variant:add", {
            bubbles: true,
            detail: { items, cart: cartContent }
          })
        );

        document.documentElement.dispatchEvent(
          new CustomEvent("cart:change", {
            bubbles: true,
            detail: {
              baseEvent: "variant:add",
              cart: cartContent
            }
          })
        );

        if (window.Rebuy?.SmartCart?.show) {
          window.Rebuy.SmartCart.show();
        } else if (window.Rebuy?.Cart?.open) {
          window.Rebuy.Cart.open();
        }

        showAddedSuccess(form, button);
        return true;
      }

      form.dispatchEvent(
        new CustomEvent("cart:error", {
          bubbles: true,
          detail: { error: responseJson.description || "Could not add to cart." }
        })
      );
      document.dispatchEvent(new CustomEvent("cart:refresh"));
      return false;
    } catch (error) {
      form.dispatchEvent(
        new CustomEvent("cart:error", {
          bubbles: true,
          detail: { error: "Could not add to cart." }
        })
      );
      return false;
    } finally {
      delete form.dataset.gatSubmitting;
      if (button) {
        button.removeAttribute("aria-busy");
      }
    }
  }

  async function triggerSubmit(button, event) {
    const form = getProductForm(button);
    if (!form || button.disabled) {
      return false;
    }

    event?.preventDefault?.();
    markPendingCartAdd(form, button);

    if (isUpgradedProductForm(form)) {
      try {
        if (typeof form.requestSubmit === "function") {
          form.requestSubmit(button);
          return true;
        }
      } catch (error) {
        // fall through to direct fetch
      }
    }

    return addToCartDirect(form, button);
  }

  function onFormSubmitCapture(event) {
    const form = event.target;
    if (!isCartAddForm(form)) {
      return;
    }
    if (isUpgradedProductForm(form)) {
      return;
    }

    const button = event.submitter || findSubmitButton(form);
    markPendingCartAdd(form, button);

    event.preventDefault();
    event.stopImmediatePropagation();
    addToCartDirect(form, button);
  }

  function onDocumentSubmitCapture(event) {
    const form = event.target;
    if (!isCartAddForm(form) || isUpgradedProductForm(form)) {
      return;
    }

    const button = event.submitter || findSubmitButton(form);
    markPendingCartAdd(form, button);

    event.preventDefault();
    event.stopImmediatePropagation();
    addToCartDirect(form, button);
  }

  function wireCartFormFallbacks(root = document) {
    root.querySelectorAll(CART_FORM_SELECTOR).forEach((form) => {
      if (!isCartAddForm(form) || form.dataset.gatCartFallbackWired === "true") {
        return;
      }
      form.dataset.gatCartFallbackWired = "true";
      form.addEventListener("submit", onFormSubmitCapture, true);
    });
  }

  function initCartFormFallbacks() {
    document.addEventListener("submit", onDocumentSubmitCapture, true);
    wireCartFormFallbacks();

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) {
            return;
          }
          if (node.matches?.(CART_FORM_SELECTOR)) {
            wireCartFormFallbacks(node.parentElement || document);
          } else if (node.querySelectorAll) {
            wireCartFormFallbacks(node);
          }
        });
      });
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });

    customElements.whenDefined("product-form").then(() => {
      wireCartFormFallbacks();
    });
  }

  function initCardVariantSync() {
    const syncFromEvent = (event) => {
      const card = event.target.closest?.("pdp-related-card");
      if (!card) {
        return;
      }

      if (
        event.target.matches?.(
          "select[data-option-position], [data-related-swatch], .pdp-related-card__swatch-label"
        )
      ) {
        window.requestAnimationFrame(() => syncCardVariant(card));
      }
    };

    document.addEventListener("change", syncFromEvent, true);
    document.addEventListener("input", syncFromEvent, true);
    document.addEventListener("blur", syncFromEvent, true);

    /**
     * At touchend iOS has not applied the radio yet, so reading :checked returns
     * the previous swatch. Commit the tapped label's own value after activation.
     */
    const commitSwatchLabel = (event) => {
      const label = event.target.closest?.(".pdp-related-card__swatch-label");
      if (!label) {
        return;
      }

      const card = label.closest("pdp-related-card");
      if (!card) {
        return;
      }

      const input =
        (label.htmlFor && card.querySelector(`#${CSS.escape(label.htmlFor)}`)) ||
        label.parentElement?.querySelector("[data-related-swatch]");
      const value = input?.value;
      if (!value) {
        return;
      }

      window.setTimeout(() => applySwatchValue(card, label, value), 0);
    };

    document.addEventListener("touchend", commitSwatchLabel, {
      capture: true,
      passive: true
    });
    document.addEventListener("click", commitSwatchLabel, true);
  }

  function applySwatchValue(card, label, value) {
    const step = label.closest("[data-option-step]");
    const position = Number(step?.dataset?.optionStep);

    if (
      typeof card.selectOptionByPosition === "function" &&
      Number.isFinite(position)
    ) {
      card.selectOptionByPosition(position, value);
      return;
    }

    if (step) {
      step.querySelectorAll("[data-related-swatch]").forEach((input) => {
        input.checked =
          normalizeOptionValue(input.value) === normalizeOptionValue(value);
      });

      const select = step.querySelector("select[data-option-position]");
      if (select) {
        const match = Array.from(select.options).find(
          (optionEl) =>
            normalizeOptionValue(optionEl.value) === normalizeOptionValue(value)
        );
        if (match) {
          select.value = match.value;
        }
      }
    }

    syncCardVariant(card);
  }

  function cancelPendingSubmit(button) {
    if (button?._gatPendingSubmitTimeout) {
      window.clearTimeout(button._gatPendingSubmitTimeout);
      delete button._gatPendingSubmitTimeout;
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initCartFormFallbacks, { once: true });
    document.addEventListener("DOMContentLoaded", initCardVariantSync, { once: true });
  } else {
    initCartFormFallbacks();
    initCardVariantSync();
  }

  document.addEventListener(
    "variant:add",
    (event) => {
      const form = formFromVariantEvent(event);
      if (!form) {
        return;
      }
      showAddedSuccess(form);
    },
    false
  );

  function onCartChangeSuccess(event) {
    if (event.detail?.baseEvent !== "variant:add") {
      return;
    }

    const pending = consumePendingCartAdd();
    if (pending?.form) {
      showAddedSuccess(pending.form, pending.button);
      return;
    }

    const form = formFromVariantEvent(event);
    if (form) {
      showAddedSuccess(form);
    }
  }

  document.addEventListener("cart:change", onCartChangeSuccess, false);
  document.documentElement.addEventListener("cart:change", onCartChangeSuccess, false);

  if (!isTouchDevice()) {
    return;
  }

  const activeTouches = new WeakMap();

  function touchMoved(start, end) {
    if (!start || !end) {
      return false;
    }
    return (
      Math.abs(end.clientX - start.clientX) > TAP_MOVE_THRESHOLD ||
      Math.abs(end.clientY - start.clientY) > TAP_MOVE_THRESHOLD
    );
  }

  function markPendingSubmit(button, form) {
    cancelPendingSubmit(button);

    const tap = { button, form, submitted: false };
    const markSubmitted = () => {
      tap.submitted = true;
    };

    form?.addEventListener("submit", markSubmitted, { once: true, capture: true });

    button._gatPendingSubmitTimeout = window.setTimeout(() => {
      form?.removeEventListener("submit", markSubmitted, { capture: true });
      delete button._gatPendingSubmitTimeout;
      if (tap.submitted) {
        return;
      }
      triggerSubmit(button);
    }, 400);
  }

  document.addEventListener(
    "touchstart",
    (event) => {
      if (isBlockedOverlayTarget(event.target)) {
        return;
      }

      const button = event.target.closest?.('button[type="submit"]');
      if (!isProductSubmitButton(button)) {
        return;
      }

      const touch = event.changedTouches?.[0] || event.touches?.[0];
      if (!touch) {
        return;
      }

      activeTouches.set(button, {
        x: touch.clientX,
        y: touch.clientY,
        scrollSurface: isScrollableSurface(button)
      });

      markPendingSubmit(button, getProductForm(button));
    },
    { capture: true, passive: true }
  );

  document.addEventListener(
    "touchend",
    (event) => {
      if (isBlockedOverlayTarget(event.target)) {
        return;
      }

      const button = event.target.closest?.('button[type="submit"]');
      if (!isProductSubmitButton(button)) {
        return;
      }

      const touch = event.changedTouches?.[0];
      const start = activeTouches.get(button);
      activeTouches.delete(button);

      if (touchMoved(start, touch)) {
        return;
      }

      const form = getProductForm(button);

      if (!touchMoved(start, touch)) {
        cancelPendingSubmit(button);

        if (
          form?.closest?.("pdp-related-card") ||
          form?.classList.contains("pdp-related-card__form")
        ) {
          triggerSubmit(button, event);
          return;
        }

        if (form && button.form !== form) {
          triggerSubmit(button, event);
          return;
        }

        if (start?.scrollSurface || isScrollableSurface(button)) {
          triggerSubmit(button, event);
        }
      }
    },
    { capture: true, passive: false }
  );

  document.addEventListener(
    "touchcancel",
    (event) => {
      const button = event.target.closest?.('button[type="submit"]');
      if (button) {
        activeTouches.delete(button);
        cancelPendingSubmit(button);
      }
    },
    { capture: true, passive: true }
  );

  document.addEventListener(
    "click",
    (event) => {
      if (isBlockedOverlayTarget(event.target)) {
        return;
      }

      const button = event.target.closest?.('button[type="submit"]');
      if (!isProductSubmitButton(button)) {
        return;
      }

      const form = getProductForm(button);
      if (!form || isUpgradedProductForm(form)) {
        return;
      }

      // Collection grids: intercept direct button clicks — Rebuy can swallow native submit.
      if (isCardCartForm(form)) {
        event.preventDefault();
        event.stopPropagation();
        triggerSubmit(button, event);
        return;
      }

      if (event.target === button) {
        return;
      }

      let handled = false;
      const markSubmitted = () => {
        handled = true;
      };

      form.addEventListener("submit", markSubmitted, { once: true, capture: true });

      queueMicrotask(() => {
        form.removeEventListener("submit", markSubmitted, { capture: true });
        if (!handled) {
          triggerSubmit(button, event);
        }
      });
    },
    { capture: true }
  );
})();
